import { useEffect, useMemo, useState } from 'react';

import {
  BarChartOutlined as TodayIcon,
  Close as CloseIcon,
  DateRangeOutlined as PeriodIcon,
  GraphicEqOutlined as VoiceIcon,
  TimelineOutlined as HistoryIcon,
} from '@mui/icons-material';
import {
  Box,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { getResident } from '@/lib/api/residents';

import { AlertReasonCard } from './AlertReasonCard';
import { LinkedAccounts } from './LinkedAccounts';

import { TabHistory } from './tabs/TabHistory';
import { TabPeriod } from './tabs/TabPeriod';
import { TabToday } from './tabs/TabToday';
import { TabVoice } from './tabs/TabVoice';

export type DetailTab = 'today' | 'period' | 'voice' | 'history';

const TABS: { value: DetailTab; label: string; Icon: typeof TodayIcon }[] = [
  { value: 'today', label: '오늘 현황', Icon: TodayIcon },
  { value: 'period', label: '브레인가드테라피', Icon: PeriodIcon },
  { value: 'voice', label: '보이스웰니스', Icon: VoiceIcon },
  { value: 'history', label: '상세 이력', Icon: HistoryIcon },
];

const TYPE_LABEL: Record<string, string> = {
  RESIDENT: '입소',
  OUTPATIENT_A: '외래 A',
  OUTPATIENT_B: '외래 B',
};

interface Props {
  residentId: string | null;
  tab: DetailTab;
  onClose: () => void;
  onTabChange: (tab: DetailTab) => void;
}

/**
 * 의뢰서 5-2 입소자 개별 상세 패널 셸 (P4-1).
 *
 * - 우측 슬라이드 Drawer (PC/태블릿 공용)
 * - 4개 탭 컨테이너 (BGD 탭은 2026-06-17 결정으로 제거)
 * - 탭별 본문은 placeholder. 실제 내용은 P4-2/Phase 5/Phase 6에서 채움
 * - 닫기는 onClose 콜백(부모가 URL state ?detail 제거) — 뒤로 가기로도 닫힘
 *
 * residentId가 null이면 닫힌 상태. open 여부는 residentId 유무로 판정.
 */
export function ResidentDetailDrawer({ residentId, tab, onClose, onTabChange }: Props) {
  const open = residentId !== null;
  const [localResidentId, setLocalResidentId] = useState<string | null>(null);

  // close 트랜지션 중에도 직전 내용을 유지하기 위해 캐시
  useEffect(() => {
    if (residentId) setLocalResidentId(residentId);
  }, [residentId]);

  const idForQuery = residentId ?? localResidentId;
  const { data, isLoading } = useQuery({
    queryKey: ['residents', 'detail', idForQuery],
    queryFn: () => (idForQuery ? getResident(idForQuery) : Promise.resolve(null)),
    enabled: idForQuery !== null,
    staleTime: 30 * 1000,
  });

  const header = useMemo(() => {
    if (!data) return null;
    const room = data.room ? `${data.room.roomNumber}호 (${data.room.floor}층)` : '미배정';
    const idLabel = data.user?.patientCode ?? '환자번호 미등록';
    const birth = data.user?.birthDate ? ` · ${data.user.birthDate}` : '';
    const age =
      data.user?.age !== null && data.user?.age !== undefined ? ` · ${data.user.age}세` : '';
    return { room, idLabel, birth, age, name: data.user?.name, type: data.type };
  }, [data]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: false }}
      sx={{ zIndex: (t) => t.zIndex.drawer + 2 }}
      slotProps={{
        paper: {
          sx: { width: { xs: '100%', sm: 560, md: 720 } },
        },
      }}
    >
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
            {isLoading || !header ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  불러오는 중…
                </Typography>
              </Stack>
            ) : (
              <>
                <Typography variant="h5">{header.room}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {header.idLabel}
                  {header.birth}
                  {header.age}
                  {header.name ? ` · ${header.name}` : ''}
                </Typography>
                <Box>
                  <Chip
                    size="small"
                    label={TYPE_LABEL[header.type] ?? header.type}
                    color={header.type === 'RESIDENT' ? 'primary' : 'default'}
                    variant="outlined"
                  />
                </Box>
                {/* 연결 계정 — 목록이 아니라 여기서 확인하고 바꾼다 */}
                {data && (
                  <Box sx={{ pt: 1.5 }}>
                    <LinkedAccounts resident={data} />
                  </Box>
                )}
                {/* 경보가 있을 때만 — 무엇을 왜 못 쓰고 있는지 */}
                {idForQuery && (
                  <Box sx={{ pt: 1.5 }}>
                    <AlertReasonCard residentId={idForQuery} onOpenTab={onTabChange} />
                  </Box>
                )}
              </>
            )}
          </Stack>
          <IconButton size="small" onClick={onClose} aria-label="상세 패널 닫기">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v: DetailTab) => onTabChange(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {TABS.map((t) => (
          <Tab
            key={t.value}
            value={t.value}
            label={t.label}
            icon={<t.Icon fontSize="small" />}
            iconPosition="start"
            sx={{ minHeight: 48 }}
          />
        ))}
      </Tabs>

      <Box sx={{ p: 3, overflow: 'auto', flex: 1 }}>
        {tab === 'today' && <TabToday resident={data ?? null} />}
        {tab === 'period' && <TabPeriod resident={data ?? null} />}
        {tab === 'voice' && <TabVoice resident={data ?? null} />}
        {tab === 'history' && <TabHistory resident={data ?? null} />}
      </Box>
    </Drawer>
  );
}
